import {useApiCall, useApiEndpoint} from "@jane/lib/src/api"
import { ContentContainer } from "@jane/lib/src/components/content/ContentContainer";
import { PageHeader } from "@jane/lib/src/components/content/PageHeader";
import { Loading } from "@jane/lib/src/components/Loading";
import React, {ReactNode, useMemo} from "react"
import {config} from "../../config";
import {usePermissions} from "../../resources/PermissionsHook";
import {NavLink, useNavigate} from "react-router-dom";
import { Button } from "@jane/lib/src/components/form/Button";
import {
  faCheckCircle,
  faChevronRight,
  faPencil,
  faPeopleGroup,
  faPerson,
  faPlus
} from "@fortawesome/free-solid-svg-icons";
import DynamicDataTable from "../../../../lib/src/components/data-table/DynamicDataTable";
import {Paragraph} from "../../../../lib/src/components/Paragraph";
import moment from "moment";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

export default function BroadcastsListPage(): ReactNode {
  const {broadcasts: broadcastsApi} = useApiCall(config)
  const {resource: broadcasts, isLoading} = useApiEndpoint(() => broadcastsApi.browse())
  const navigate = useNavigate()

  const permissions = usePermissions()

  const sortedBroadcasts = useMemo(() => {
    return (broadcasts ?? []).sort((a,b) => {
      if (a.status === 'published' && b.status === 'published') return moment(b.updated_at).isAfter(a.updated_at) ? 1 : -1
      if (a.status === 'published') return 1
      if (b.status === 'published') return -1
      return moment(b.updated_at).isAfter(a.updated_at) ? 1 : -1
    })
  }, [broadcasts])

  if (! permissions.canManageBroadcasts) {
    return <ContentContainer size={'lg'}>
      <PageHeader>Berichten</PageHeader>
      <Paragraph>
        Berichten zijn verplaatst naar <NavLink to={'/tussen-ons'} className={'text-brand-primary underline'}>TussenOns</NavLink>.
      </Paragraph>
    </ContentContainer>;
  }

  return <div>
    <ContentContainer>
      <PageHeader>Publicaties</PageHeader>
      <div className={"flex items-end"}>
        <p className={"flex-1"}>
          Met publicaties kun je berichten sturen naar clienten of medewerkers binnen een bepaalde regio, of allemaal tegelijk.
        </p>
        <Button type={'primary'} size={'sm'} text={'Nieuwe publicatie'} icon={faPlus} onClick={() => navigate('/broadcasts/new')} />
      </div>

      <Loading loading={isLoading}>
        {sortedBroadcasts && <DynamicDataTable placeholder={'Geen publicaties'} searchableProperties={["subject", "message"]} availableFilters={[
          {
            title: "Status",
            property: "status",
            options: {
              "all": "Alle",
              "draft": "Concept",
              "published": "Verzonden",
            },
            filterCallback: (row, filterValue) => filterValue === 'all' || row.status === filterValue,
            defaultValue: "all"
          },
          {
            title: "Aan",
            property: "target_type",
            options: {
              "all": "Alle",
              "clients": "Cliënten",
              "employees": "Medewerkers",
            },
            filterCallback: (row, filterValue) => filterValue === 'all' || row.target_type === filterValue,
            defaultValue: "all"
          },
          {
            title: "Laatst geupdate",
            property: "updated_at",
            options: {
              "all": "Alle",
              "30": "< 30 dagen",
              "90": "< 90 dagen",
              "365": "< 1 jaar",
            },
            filterCallback: (row, filterValue) => filterValue === 'all' || moment(row.updated_at).isSameOrAfter(moment().subtract(parseInt(filterValue), 'days')),
            defaultValue: "90"
          }
        ]} keyProperty={'id'} data={sortedBroadcasts} columns={[
          {
            property: 'subject',
            header: 'Bericht',
            transform: (text, row) => <div>
              <div className={'font-medium'}>{text.length > 60 ? text.substring(0,60) + '…' : text}</div>
              <div className={'font-normal text-xs text-slate-600'}>{row.message.length > 80 ? row.message.substring(0,80) + '…' : row.message}</div>
            </div>,
          },
          {
            property: 'status',
            header: 'Status',
            transform: (status) => status === "draft" ? <div className={'flex items-center text-slate-500'}>
              <div className={'w-6'}><FontAwesomeIcon icon={faPencil} /></div> Concept
            </div> : <div className={'flex items-center text-green-600'}>
              <div className={'w-6'}><FontAwesomeIcon icon={faCheckCircle} /></div> Verzonden
            </div>,
          },
          {
            property: 'target_type',
            header: 'Aan',
            transform: (location, row) => row.target_type === "clients" ? <div className={'flex items-center text-brand-secondary'}>
              <div className={'w-6'}><FontAwesomeIcon icon={faPerson} /></div> Cliënten
            </div> : <div className={'flex items-center text-brand-secondary'}>
              <div className={'w-6'}><FontAwesomeIcon icon={faPeopleGroup} /></div> Medewerkers
            </div>,
          },
          {
            property: 'updated_at',
            header: 'Bewerkt',
            transform: (updated) => <div className={'whitespace-nowrap'}>{moment(updated).format('DD-MM-YYYY HH:mm')}</div>,
          },
          {header: "Acties", property: 'id', transform: (id, row) => {
              return <Button type={'secondary'} size={'xs'} text={row.status === 'draft' ? 'Bewerken' : 'Bekijken'} icon={faChevronRight} onClick={() => navigate(`/broadcasts/${id}`)} />
            }},
        ]} /> }

      </Loading>
    </ContentContainer>
  </div>
}
